import React, { useEffect, useState } from "react";
import { useUploadFile } from "../hooks/use-upload-file";
import { useTemplate } from "../hooks/use-template";
import { useBroadcast } from "../hooks/use-broadcast";
import { BROADCAST_SEND_TYPE } from "../constants/broadcast";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  BroadcastAppCard,
  ContainerActions,
  DetailItem,
  DetailLabel,
  DetailsIcon,
  DetailsPanel,
  DetailsPanelTitle,
  DetailValue,
  ResumeContainer,
} from "../styles";
import { WhatsAppPreview } from "./whatsapp-preview";
import { CampaignUploadProgress } from "./campaign-upload-progress";
import { BackLink } from "../../Campaigns.style";
import Button from "../../../../../components/shared/components/button/Button.style";

export const ResumeBroadcast = ({ handleNext, handleBack, broadcastData }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actionUploadStates, setActionUploadStates] = useState([]);

  const {
    handleUploadFile,
    isLoading: isLoadingUploadFile,
    error: errorUploadFile,
    isUploadedFile,
  } = useUploadFile();

  const {
    createTemplate,
    isLoading: isLoadingTemplate,
    error: errorTemplate,
    isTemplateCreated,
  } = useTemplate();

  const {
    createBroadcast,
    isLoading: isLoadingBroadcast,
    error: errorBroadcast,
    isSentBroadcast,
  } = useBroadcast();

  const {
    broadcastName,
    campaignCost,
    campaignCostData,
    phonesToNotify,
    contacts,
    sendType,
    scheduledDate,
    scheduledTime,
    bodyMessage,
    bodyMessageWithFormat,
    footerMessage,
    buttons,
    templateId,
    templateName,
    templateVariables,
    templateCategory,
    templateFile,
    templateFileUrl,
    templateFileType,
    actions,
    tagId,
    tagName,
  } = broadcastData;

  useEffect(() => {
    if (!actions) return;
    setActionUploadStates(
      actions.map((a) => ({ hasFile: Boolean(a.mediaFile), loading: false, done: false }))
    );
  }, [actions]);

  useEffect(() => {
    if (templateFileUrl) {
      setPreviewUrl(templateFileUrl);
      return;
    }
    if (!templateFile) return;
    const url = URL.createObjectURL(templateFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [templateFile]);

  const uploadActionFile = async (file, index) => {
    setActionUploadStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, loading: true } : s))
    );
    const url = await handleUploadFile(file);
    setActionUploadStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, loading: false, done: Boolean(url) } : s))
    );
    return url;
  };

  const handleSubmit = async () => {
    window.scrollTo({ bottom: 0, behavior: "smooth" });

    let templateMediaUrl = templateFileUrl;
    if (templateFile && !templateFileUrl) {
      templateMediaUrl = await handleUploadFile(templateFile);
    }

    if (!templateId) {
      const newTemplate = await createTemplate({
        templateName,
        templateCategory,
        bodyMessage,
        templateFile,
        templateFileUrl: templateMediaUrl,
        templateVariables,
        footerMessage,
        templateButtons: buttons,
      });
      if (!newTemplate) return;
    }

    let resolvedActions = actions ? [...actions] : [];

    const actionsWithFiles = resolvedActions
      .map((action, index) => ({ action, index }))
      .filter(({ action }) => action.mediaFile);

    if (actionsWithFiles.length > 0) {
      const uploadResults = await Promise.all(
        actionsWithFiles.map(({ action, index }) =>
          uploadActionFile(action.mediaFile, index).then((url) => ({ index, url }))
        )
      );
      uploadResults.forEach(({ index, url }) => {
        if (url) {
          resolvedActions[index] = { ...resolvedActions[index], mediaUrl: url };
        }
      });
    }

    const actionsPayload = resolvedActions.map(({ mediaFile, ...rest }) => rest);

    const newBroadcast = await createBroadcast({
      broadcastName,
      templateName,
      templateFileUrl: templateMediaUrl,
      sendType,
      scheduledDate,
      scheduledTime,
      contacts,
      actions: actionsPayload,
      phonesToNotify,
      templateNew: !templateId,
      tagId,
    });

    if (!newBroadcast) return;

    setTimeout(() => handleNext(), 500);
  };

  const getScheduleText = () => {
    if (sendType === BROADCAST_SEND_TYPE.NOW) return "Envío inmediato";
    return `Programado para ${scheduledDate} a las ${scheduledTime}`;
  };

  const hasActionFiles = actionUploadStates.some((s) => s.hasFile);
  const isLoadingActionFiles = actionUploadStates.some((s) => s.loading);

  const fileStatusText = () => {
    if (isLoadingUploadFile && !isLoadingActionFiles) return "Subiendo archivo de plantilla...";
    if (isUploadedFile && !isLoadingActionFiles) return "Archivo subido exitosamente";
    return "Subir archivo multimedia de plantilla";
  };

  const templateStatusText = () => {
    if (isLoadingTemplate) return "Creando plantilla de WhatsApp...";
    if (isTemplateCreated) return "Plantilla creada exitosamente";
    return "Crear plantilla de WhatsApp";
  };

  const broadcastStatusText = () => {
    if (isLoadingBroadcast) return "Creando campaña de difusión...";
    if (isSentBroadcast) return "Campaña creada exitosamente";
    return "Crear campaña de difusión";
  };

  const buttonSendText = () => {
    if (isLoadingUploadFile && !isLoadingActionFiles) return "Subiendo plantilla...";
    if (isLoadingActionFiles) {
      const doneCount = actionUploadStates.filter((s) => s.done).length;
      const totalCount = actionUploadStates.filter((s) => s.hasFile).length;
      return `Subiendo archivos (${doneCount}/${totalCount})...`;
    }
    if (isLoadingTemplate) return "Creando plantilla...";
    if (isLoadingBroadcast) return "Creando campaña...";
    if (isTemplateCreated && isSentBroadcast) return "Campaña creada ✓";
    return "Enviar Campaña";
  };

  const error = errorUploadFile || errorTemplate || errorBroadcast;
  const isLoading = isLoadingUploadFile || isLoadingTemplate || isLoadingBroadcast || isLoadingActionFiles;
  const isCreated = isTemplateCreated || isSentBroadcast || isUploadedFile;

  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <ResumeContainer>
          <DetailsPanel>
            <DetailsPanelTitle>
              <DetailsIcon />
              Detalles
            </DetailsPanelTitle>

            <DetailItem>
              <DetailLabel>Nombre de campaña</DetailLabel>
              <DetailValue>{broadcastName}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Programación</DetailLabel>
              <DetailValue>{getScheduleText()}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Plantilla</DetailLabel>
              <DetailValue>{templateName}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Contactos</DetailLabel>
              <DetailValue>{contacts.length}</DetailValue>
            </DetailItem>

            {tagName && (
              <DetailItem>
                <DetailLabel>Etiqueta a asignar</DetailLabel>
                <DetailValue>{tagName}</DetailValue>
              </DetailItem>
            )}

            <DetailItem>
              <DetailLabel>
                {campaignCostData?.provider === "TP"
                  ? "Comisión por servicio"
                  : "Costo estimado"}
              </DetailLabel>
              <DetailValue>${campaignCost} USD</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Variables del mensaje</DetailLabel>
              {templateVariables && templateVariables.length > 0 ? (
                <DetailValue
                  style={{ display: "flex", flexDirection: "column", gap: "0.25rem", fontSize: "0.813rem" }}
                >
                  {templateVariables.map((variable, index) => (
                    <span
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        backgroundColor: "#f0f9f7",
                        borderRadius: "4px",
                        border: "1px solid #128c7e",
                      }}
                    >
                      <strong style={{ color: "#128c7e", marginRight: "4px" }}>
                        Variable {index + 1}
                      </strong>
                      <span style={{ color: "#666" }}>→ {variable}</span>
                    </span>
                  ))}
                </DetailValue>
              ) : (
                <DetailValue>Sin variables</DetailValue>
              )}
            </DetailItem>

            {hasActionFiles && (
              <DetailItem>
                <DetailLabel>Archivos por botón</DetailLabel>
                <DetailValue style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  {actions.map((action, i) =>
                    action.mediaFile ? (
                      <span
                        key={i}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          padding: "4px 8px",
                          backgroundColor: "#f0f9f7",
                          borderRadius: "4px",
                          border: "1px solid #128c7e",
                          fontSize: "0.8rem",
                        }}
                      >
                        <strong style={{ color: "#128c7e" }}>Botón {i + 1}</strong>
                        <span style={{ color: "#555" }}>📎 {action.mediaFile.name}</span>
                      </span>
                    ) : null
                  )}
                </DetailValue>
              </DetailItem>
            )}
          </DetailsPanel>

          <WhatsAppPreview
            fileType={templateFileType}
            fileName={templateFile?.name || null}
            previewUrl={previewUrl}
            textWithFormat={bodyMessageWithFormat}
            footerMessage={footerMessage}
            buttons={buttons}
          />
        </ResumeContainer>

        {(isLoading || isCreated) && (
          <CampaignUploadProgress
            templateFile={templateFile}
            isUploadedFile={isUploadedFile}
            isLoadingUploadFile={isLoadingUploadFile}
            isLoadingActionFiles={isLoadingActionFiles}
            actionUploadStates={actionUploadStates}
            isTemplateCreated={isTemplateCreated}
            isLoadingTemplate={isLoadingTemplate}
            isSentBroadcast={isSentBroadcast}
            isLoadingBroadcast={isLoadingBroadcast}
            fileStatusText={fileStatusText}
            templateStatusText={templateStatusText}
            broadcastStatusText={broadcastStatusText}
          />
        )}

        <ContainerActions style={{ marginTop: "1rem" }}>
          <BackLink
            onClick={handleBack}
            style={{ pointerEvents: isLoading ? "none" : "auto", opacity: isLoading ? 0.5 : 1 }}
          >
            ← Atrás
          </BackLink>
          <Button
            designV4
            onClick={handleSubmit}
            disabled={isLoading || (isTemplateCreated && isSentBroadcast)}
          >
            {buttonSendText()}
          </Button>
        </ContainerActions>

        {error && (
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {error}
          </div>
        )}
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
